import { AppointmentService } from '../../api/services/AppointmentService';
import { CompanySettingsService } from '../../api/services/CompanySettingsService';
import { CompanyLocationService } from '../../api/services/CompanyLocationService';
import { CompanyWorkScheduleService } from '../../api/services/CompanyWorkScheduleService';
import { UserService } from '../../api/services/UserService';
import { SmsIntegrationService } from '../../api/services/SmsIntegrationService';
import { System_DayOfWeek } from '../../api/models/System_DayOfWeek';
import { Routemize_Constants_WorkingHourStatus } from '../../api/models/Routemize_Constants_WorkingHourStatus';
import type { Routemize_DTOs_AppointmentSummary } from '../../api/models/Routemize_DTOs_AppointmentSummary';
import type { Routemize_DTOs_UserWorkingHourOverrideDTO } from '../../api/models/Routemize_DTOs_UserWorkingHourOverrideDTO';
import type { Routemize_DTOs_UserWorkingHourDTO } from '../../api/models/Routemize_DTOs_UserWorkingHourDTO';
import type { Routemize_DTOs_EnhancedWorkScheduleDTO } from '../../api/models/Routemize_DTOs_EnhancedWorkScheduleDTO';
import { resolveRecurringDayHours } from '../availability/UserWorkScheduleManager';
import type { PlanTier } from '../billing/featureCatalog';
import type { Routemize_DTOs_TeamMemberListDto } from '../../api/models/Routemize_DTOs_TeamMemberListDto';
import type { Routemize_DTOs_CompanyLocationDto } from '../../api/models/Routemize_DTOs_CompanyLocationDto';
import { timelineItemsToAppointments } from '../../utils/stopGapFinder';
import { getLocalDateParts, formatWeekdayLong } from '../../utils/timeUtils';
import type { Appointment } from '../../components/timeline/types';

/**
 * Shared environment Routy's tools operate against. Sourced from React
 * contexts at the call site (AppConfig.currentUser, CompanyData.timezone) and
 * passed into the agent so the pure tool/business layer stays UI-agnostic.
 */
export interface RoutyEnvironment {
  /** The operator the assistant is scheduling for (defaults to current user). */
  currentUserId: string;
  /** Operator display name, used in prompts/copy. */
  currentUserName?: string;
  /** IANA timezone for the tenant (e.g. "America/New_York"). */
  companyTimezone: string | undefined;
  /**
   * Operator's effective route START address (display string), per the
   * company's Route Preferences setting (home vs. business/office address) -
   * see `fetchRouteEndpoints`. If unset, callers should fall back gracefully.
   */
  startingAddress?: string;
  /** Operator's effective route START coordinates, used to bias place search + routing. */
  startingCoordinates?: { lat: number; lng: number };
  /**
   * Short human label for the route start used in drive-leg copy, e.g.
   * "your current location" when the operator opted into the live-GPS start
   * override. Defaults to "your starting location" when unset.
   */
  startingLabel?: string;
  /** Operator's effective route END address (display string), per Route Preferences. */
  endingAddress?: string;
  /** Operator's effective route END coordinates. */
  endingCoordinates?: { lat: number; lng: number };
  /**
   * Whether the operator can view/manage OTHER team members' bookings (the same
   * permission that gates the Timeline's team-member selector and Smart Fill for
   * others: `Routemize.Appointment.Update`, see `usePermissions().canManageAllBookings`).
   * Tools that accept an `assignedUserId` for someone other than `currentUserId`
   * require this to be true.
   */
  canManageAllBookings: boolean;
  /**
   * Whether the tenant has a connected SMS provider (Twilio or OpenPhone), per
   * `useSmsProviderStatus`/Settings > Integrations. Texting a customer silently
   * fails on the backend when nothing is connected, so tools must check this
   * before offering an SMS notify option - the same guard Create Booking / Edit
   * Event / On The Way already apply.
   */
  smsProviderConnected: boolean;
  /**
   * Number of ACTIVE team members on the account, INCLUDING the operator (see
   * `fetchTeamContext`). Lets the system prompt distinguish a solo operator
   * ("our week" can only mean their own) from a multi-member team (where
   * "our/we/the team" is ambiguous and needs one clarifying question).
   * Undefined when the lookup failed - treat as unknown, not as solo.
   */
  activeTeamMemberCount?: number;
  /** Display names of the active team members (capped), for prompt context. */
  teamMemberNames?: string[];
  /**
   * Can change company settings, hours, branding and locations, and invite or
   * change team members (`usePermissions().canEditCompanySettings`).
   */
  canEditCompanySettings: boolean;
  /** Can change existing booking forms, their settings and questions. */
  canEditBookingForms: boolean;
  /** Can create booking forms. */
  canCreateBookingForms: boolean;
  /** The tenant's plan; null or undefined while unknown. */
  planTier?: PlanTier | null;
  /** featureCatalog keys the plan doesn't include, from `ROUTY_SETUP_FEATURES`. */
  lockedFeatures?: string[];
  /**
   * Whether Routy may talk about plans, pricing, upgrades and buying seats, or link to billing.
   * Off in the mobile app: App Store guidelines 3.1.1 and 3.1.3(d) bar it from offering a
   * purchase or pointing anywhere else to make one. Missing counts as off.
   */
  canMentionBilling?: boolean;
}

/** Plan-gated features the setup tools check before writing. */
export const ROUTY_SETUP_FEATURES = ['SameDayBookings', 'SmartAssignment', 'FormPrequalification', 'CustomTerritories'] as const;

/** Team-size context for the system prompt (see RoutyEnvironment). */
export interface TeamContext {
  activeMemberCount: number;
  memberNames: string[];
}

/**
 * Fetch how many ACTIVE team members the account has (operator included) and
 * their names, so the agent knows whether "our/we/the team" could mean anyone
 * other than the operator. Best-effort: returns null on failure so callers
 * leave the env fields undefined (unknown) rather than claiming a solo account.
 */
export async function fetchTeamContext(): Promise<TeamContext | null> {
  try {
    const members = (await UserService.getApiAppUserGetTeamMembers()) as Routemize_DTOs_TeamMemberListDto[];
    const active = (members ?? []).filter((m) => m.isActive !== false);
    return {
      activeMemberCount: active.length,
      memberNames: active.map((m) => (m.name || '').trim()).filter(Boolean).slice(0, 12),
    };
  } catch {
    return null;
  }
}

export interface StartingLocation {
  address?: string;
  coordinates?: { lat: number; lng: number };
}

export interface RouteEndpoints {
  start: StartingLocation;
  end: StartingLocation;
}

function toStartingLocation(
  entity: { fullAddress?: string | null; addressLine1?: string | null; latitude?: number | null; longitude?: number | null } | undefined,
): StartingLocation {
  if (!entity) return {};
  const coordinates =
    entity.latitude != null && entity.longitude != null ? { lat: entity.latitude, lng: entity.longitude } : undefined;
  return { address: entity.fullAddress || entity.addressLine1 || undefined, coordinates };
}

/**
 * Resolve a user's effective route START and END addresses, respecting the
 * company's Route Preferences (Settings > Route Preferences > "Start from" /
 * "End at": Home address vs. Business address) - the same setting that drives
 * the Timeline's "Starting Point" / "Return to Home" pills
 * (`Routemize_ValueObjects_RoutePreferences.defaultStartingLocation` /
 * `defaultEndingLocation`, `'home' | 'office'`). Falls back to 'home' if the
 * setting is unset, and to whichever piece resolves when data is missing.
 */
export async function fetchRouteEndpoints(userId: string): Promise<RouteEndpoints> {
  try {
    const [settings, members, locations] = await Promise.all([
      CompanySettingsService.getApiAppCompanySettingsGet().catch(() => null),
      UserService.getApiAppUserGetTeamMembers().catch(() => [] as Routemize_DTOs_TeamMemberListDto[]),
      CompanyLocationService.getApiAppCompanyLocationGetAllActive().catch(() => [] as Routemize_DTOs_CompanyLocationDto[]),
    ]);

    const startPref = (settings?.routePreferences?.defaultStartingLocation as 'home' | 'office' | null) ?? 'home';
    const endPref = (settings?.routePreferences?.defaultEndingLocation as 'home' | 'office' | null) ?? 'home';

    const me = members.find((m) => m.id === userId);
    const homeLocation = toStartingLocation(me?.homeAddress ?? undefined);

    const officeEntity = locations.find((l) => l.isDefault) ?? locations[0];
    const officeLocation = toStartingLocation(officeEntity);

    return {
      start: startPref === 'office' ? officeLocation : homeLocation,
      end: endPref === 'office' ? officeLocation : homeLocation,
    };
  } catch {
    return { start: {}, end: {} };
  }
}

/**
 * Resolve just the START location (back-compat helper for call sites that
 * only need one point). Prefer `fetchRouteEndpoints` when both endpoints matter.
 */
export async function fetchStartingLocation(userId: string): Promise<StartingLocation> {
  return (await fetchRouteEndpoints(userId)).start;
}

/**
 * Whether the tenant has a connected SMS provider (Twilio or OpenPhone). Mirrors
 * `useSmsProviderStatus`'s `isConnected` check but as a plain async call, since
 * Routy's tool layer runs outside React and can't use hooks.
 */
export async function fetchSmsProviderConnected(): Promise<boolean> {
  try {
    const settings = await SmsIntegrationService.getApiAppSmsIntegrationGetSettings();
    return settings.isConnected ?? false;
  } catch {
    return false;
  }
}

export interface WorkingHours {
  start: string; // HH:MM
  end: string;   // HH:MM
  isWorkDay: boolean;
  /**
   * True when this weekday is governed by the person's OWN weekly availability
   * (the Availability schedule) rather than the company schedule.
   */
  isPersonalSchedule?: boolean;
}

const DEFAULT_HOURS: WorkingHours = { start: '08:00', end: '18:00', isWorkDay: true };

/**
 * System_DayOfWeek to JS Date.getDay() (0 = Sunday). Mirrors the mapping in
 * AddStopModal so working-hours lookups line up.
 */
const DAY_OF_WEEK_TO_JS: Array<[System_DayOfWeek, number]> = [
  [System_DayOfWeek.SUNDAY, 0],
  [System_DayOfWeek.MONDAY, 1],
  [System_DayOfWeek.TUESDAY, 2],
  [System_DayOfWeek.WEDNESDAY, 3],
  [System_DayOfWeek.THURSDAY, 4],
  [System_DayOfWeek.FRIDAY, 5],
  [System_DayOfWeek.SATURDAY, 6],
];

function dayOfWeekToJs(dow: System_DayOfWeek): number {
  return DAY_OF_WEEK_TO_JS.find(([enumDay]) => enumDay === dow)?.[1] ?? -1;
}

/**
 * A person's recurring weekly availability rows. Best-effort: a failed fetch
 * falls back to the company schedule alone rather than failing every schedule
 * lookup in the tool that asked.
 */
async function fetchUserRecurringHours(userId?: string): Promise<Routemize_DTOs_UserWorkingHourDTO[]> {
  if (!userId) return [];
  try {
    return (await UserService.getApiAppUserGetUserWorkHourAysnc(userId)) ?? [];
  } catch {
    return [];
  }
}

/**
 * Fetch the work schedule once. Returns a JS-day-indexed lookup of working
 * hours so callers can resolve hours for any date without refetching.
 *
 * Pass the person the lookup is for: their own weekly availability overrides
 * the company schedule, exactly as the Availability calendar and the public
 * booking form resolve it (UserWorkScheduleManager.getEffectiveSchedule).
 * Without the userId a weekday the person has switched off still reads as a
 * normal working day here, while customers see no slots for it on the booking
 * form - the two views disagree and Routy blames whatever else is on the day.
 */
export async function fetchWorkScheduleByDay(userId?: string): Promise<Map<number, WorkingHours>> {
  const [schedule, recurringHours]: [Routemize_DTOs_EnhancedWorkScheduleDTO, Routemize_DTOs_UserWorkingHourDTO[]] =
    await Promise.all([
      CompanyWorkScheduleService.getApiAppCompanyWorkScheduleGetEnhancedWorkSchedule(),
      fetchUserRecurringHours(userId),
    ]);

  const byDay = new Map<number, WorkingHours>();
  for (const daily of schedule.dailySchedules ?? []) {
    if (daily.dayOfWeek === undefined) continue;
    const jsDay = dayOfWeekToJs(daily.dayOfWeek);
    if (jsDay < 0) continue;
    byDay.set(jsDay, {
      start: daily.startTime || DEFAULT_HOURS.start,
      end: daily.endTime || DEFAULT_HOURS.end,
      isWorkDay: daily.isWorkDay !== false && !!daily.startTime && !!daily.endTime,
    });
  }

  for (const [dayOfWeek, jsDay] of DAY_OF_WEEK_TO_JS) {
    const personal = resolveRecurringDayHours(recurringHours, dayOfWeek);
    if (!personal) continue;
    const company = byDay.get(jsDay) ?? DEFAULT_HOURS;
    byDay.set(
      jsDay,
      personal.isWorkDay
        ? { start: personal.startTime, end: personal.endTime, isWorkDay: true, isPersonalSchedule: true }
        : { start: company.start, end: company.end, isWorkDay: false, isPersonalSchedule: true },
    );
  }

  return byDay;
}

/**
 * Resolve working hours for a specific date from a day-indexed schedule map.
 * Falls back to the default 8-6 window when the schedule is missing.
 */
export function getWorkingHoursForDate(
  date: Date,
  scheduleByDay: Map<number, WorkingHours>,
): WorkingHours {
  return scheduleByDay.get(date.getDay()) ?? DEFAULT_HOURS;
}

/**
 * A user's TRUE availability for one date: the company weekly schedule with
 * that user's date-specific working-hour overrides applied - day-off/vacation
 * blocks (written by Routy's block_time/block_days_off and the Availability
 * calendar) and "available on a company day off" overrides. This is what
 * distinguishes "off that day" from "working day with nothing booked".
 */
export interface EffectiveDayAvailability extends WorkingHours {
  /**
   * Set when the day is off because of a DATE-SPECIFIC override (not the
   * weekly schedule) - holds the override's label, e.g. "Day Off" or "Vacation".
   */
  dayOffReason?: string;
  /**
   * What makes the day off, so callers can explain it instead of just
   * reporting "not a working day":
   * - `dateOverride`: this one date is blocked (see dayOffReason);
   * - `personalWeekly`: this weekday is off on the person's own availability;
   * - `companyWeekly`: this weekday is off on the company schedule.
   */
  dayOffSource?: 'dateOverride' | 'personalWeekly' | 'companyWeekly';
  /** Partial UNAVAILABLE windows within an otherwise working day (lunch, out-of-office). */
  blockedWindows?: Array<{ start: string; end: string; reason: string }>;
}

/**
 * Fetch ALL of a user's working-hour overrides, grouped by date key
 * (YYYY-MM-DD). One call covers every date, so day/week loops reuse the map.
 * Best-effort: an override fetch failure falls back to the weekly schedule
 * alone rather than failing the whole schedule lookup.
 */
export async function fetchUserOverridesByDate(
  userId: string,
): Promise<Map<string, Routemize_DTOs_UserWorkingHourOverrideDTO[]>> {
  const byDate = new Map<string, Routemize_DTOs_UserWorkingHourOverrideDTO[]>();
  try {
    const overrides = await UserService.getApiAppUserGetUserWorkHourOverrideAysnc(userId);
    for (const o of overrides ?? []) {
      if (!o.date || o.isEnabled === false) continue;
      const list = byDate.get(o.date) ?? [];
      list.push(o);
      byDate.set(o.date, list);
    }
  } catch {
    // Unknown overrides - callers proceed with the weekly schedule only.
  }
  return byDate;
}

/**
 * Apply a user's date-specific overrides on top of the company weekly
 * schedule for one date. Rules mirror how the Availability calendar and the
 * scheduling engine treat overrides:
 * - an AVAILABLE override makes a company day-off a working day (with the
 *   override's hours);
 * - an UNAVAILABLE override covering the whole working window (or the whole
 *   day) marks the day OFF, keeping the override's label as the reason;
 * - smaller UNAVAILABLE windows (lunch, rest-of-day) stay listed as blocked
 *   windows within a working day.
 */
export function getEffectiveDayAvailability(
  date: Date,
  scheduleByDay: Map<number, WorkingHours>,
  overridesByDate: Map<string, Routemize_DTOs_UserWorkingHourOverrideDTO[]>,
): EffectiveDayAvailability {
  const base = getWorkingHoursForDate(date, scheduleByDay);
  const overrides = overridesByDate.get(toDateKey(date)) ?? [];

  let hours: WorkingHours = { ...base };

  // AVAILABLE overrides first: they can turn a company day-off into a working
  // day (or widen/replace the hours on a working day).
  const available = overrides.find(
    (o) => o.status === Routemize_Constants_WorkingHourStatus.AVAILABLE && o.startTime && o.endTime,
  );
  if (available) {
    hours = { start: available.startTime!, end: available.endTime!, isWorkDay: true };
  }

  if (!hours.isWorkDay) {
    return { ...hours, dayOffSource: hours.isPersonalSchedule ? 'personalWeekly' : 'companyWeekly' };
  }

  const unavailable = overrides.filter(
    (o) => o.status === Routemize_Constants_WorkingHourStatus.UNAVAILABLE && o.startTime && o.endTime,
  );
  // Full-day (or full-working-window) block = the day is off.
  const fullDayBlock = unavailable.find((o) => o.startTime! <= hours.start && o.endTime! >= hours.end);
  if (fullDayBlock) {
    return {
      ...hours,
      isWorkDay: false,
      dayOffReason: fullDayBlock.windowName || 'Day Off',
      dayOffSource: 'dateOverride',
    };
  }

  const blockedWindows = unavailable
    .filter((o) => o.endTime! > hours.start && o.startTime! < hours.end)
    .map((o) => ({ start: o.startTime!, end: o.endTime!, reason: o.windowName || 'Blocked' }));
  return blockedWindows.length > 0 ? { ...hours, blockedWindows } : hours;
}

/**
 * Why a date is off, phrased for the model so it explains the real cause
 * instead of guessing. The personal-weekly case is the one Routy used to miss
 * entirely: the weekday is switched off on the person's Availability schedule,
 * so the booking form offers NO slots on ANY date that lands on that weekday -
 * clearing calendar events or cancelling appointments changes nothing.
 */
export function describeDayOff(
  hours: EffectiveDayAvailability,
  date: Date,
  /** Possessive for the person the day belongs to, e.g. "your" or "Chris's". */
  possessive: string,
): string {
  const weekday = formatWeekdayLong(date);
  // Callers holding a plain WorkingHours (no date-override pass) still get the
  // right weekly explanation.
  const source =
    hours.dayOffSource ??
    (hours.dayOffReason ? 'dateOverride' : hours.isPersonalSchedule ? 'personalWeekly' : 'companyWeekly');
  if (source === 'dateOverride') {
    return (
      `OFF - this specific date is marked unavailable ("${hours.dayOffReason}"), overriding the weekly ` +
      'schedule. Other dates on the same weekday are unaffected.'
    );
  }
  if (source === 'personalWeekly') {
    return (
      `OFF - ${weekday}s are switched off in ${possessive} weekly availability schedule, so EVERY ${weekday} ` +
      'is unavailable, not just this one. Customers see no bookable slots on the booking form for it, and no ' +
      'amount of clearing calendar events or appointments will open it up - the weekday has to be turned back ' +
      'on from the Availability page in the sidebar. Lead with this reason before anything else on the day.'
    );
  }
  return (
    `OFF - ${weekday}s are not working days on the COMPANY schedule, so every ${weekday} is unavailable and ` +
    'customers see no bookable slots for it. Opening it up means changing the company work hours in Settings, ' +
    'not clearing anything off the day.'
  );
}

/**
 * Format a YYYY-MM-DD key for a Date using its local parts (no UTC shift).
 */
export function toDateKey(date: Date): string {
  const { year, month, day } = getLocalDateParts(date);
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * Fetch a single day's appointments for the operator, normalized to the
 * Appointment shape the gap finder and schedule summaries expect.
 *
 * Uses /api/app/appointment/getUserTimeline (the canonical user- and
 * date-scoped endpoint) exactly like AddStopModal — never the search endpoint,
 * whose typed filter silently ignores date/user scoping.
 */
export async function fetchDayAppointments(
  userId: string,
  date: Date,
  timezone: string | undefined,
): Promise<Appointment[]> {
  const dateStr = toDateKey(date);
  const response = await AppointmentService.getApiAppAppointmentGetUserTimeline(
    userId,
    undefined,
    undefined,
    undefined,
    dateStr,
  );
  const items = (response?.items || []) as Array<{ appointment?: Routemize_DTOs_AppointmentSummary }>;
  return timelineItemsToAppointments(items, timezone);
}
